import {
  Autocomplete,
  Button,
  IconButton,
  Popover,
  TextField,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import CustomSearchbar from "../custom-searchbar/CustomSearchbar";
import PropTypes from "prop-types";
import { useState } from "react";
import {
  CrossIconTask,
  PendingExport,
  PendingFilter,
} from "../../assets/common-svg/CommonSvg";
import { useFormik } from "formik";
import CustomToggleSwitch from "../toggle-switch/CustomToggleSwitch";
import CustomDateRangePicker from "../custom-date-picker/CustomDateRangePicker";
import { PendingTasks } from "../../utils/formik/initialValues";
import { useSelector } from "react-redux";

export const PendingTaskHeader = ({
  taskCount,
  handleGetHistoryData,
  alignment,
  setAlignment,
  setPage,
  SearchbarPlaceholder,
  handleSearch,
  value,
  disabled,
  handleExportButton = () => {},
  requestData,
  rolesData=[],
  phaseData,
  productData,
  handleFilterSubmit,
  setFilters,
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [resetCount, setResetCount] = useState(0);
  const role_id = useSelector((state) => state.loginData.role_id);

  const open = Boolean(anchorEl);

  let leftCount = taskCount?.stats?.pending_count;
  let RightCount = taskCount?.stats?.closed_count;

  const handleDateChange = (e) => {
    setSelectedDate(e);
  };

  const formik = useFormik({
    initialValues: PendingTasks,
    onSubmit: (values) => {
      handleFilterSubmit(values, selectedDate);
    },
  });

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleClearFilter = () => {
    setSelectedDate(null);
    setFilters({});
    formik.resetForm();
    setResetCount((prev) => prev + 1); // Increment to trigger reset
    if (alignment.includes("Pending")) {
      handleGetHistoryData("0");
    } else {
      handleGetHistoryData("1");
    }
    handlePopoverClose();
  };

  return (
    <div className="w-full grid lg:grid-cols-12 gap-4 relative">
      <div className="col-start-1 col-end-7 sm:col-end-4 w-100">
        {handleSearch && (
          <CustomSearchbar
            value={value}
            Placeholder={SearchbarPlaceholder}
            handleSearch={handleSearch}
            disabled={disabled}
          />
        )}
      </div>
      <div className="col-start-7 sm:col-start-4 col-end-13 flex justify-end items-center gap-2 relative">
        {role_id != 1 && (
          <CustomToggleSwitch
            leftText="Pending Tasks"
            rightText="Closed Tasks"
            disabled={false}
            setAlignment={setAlignment}
            alignment={alignment}
            toggleWidth="min-w-fit"
            onLeftClick={() => {
              setPage(1);
            }}
            onRightClick={() => {
              setPage(1);
            }}
            leftCount={leftCount}
            RightCount={RightCount}
          />
        )}
        {role_id === 1 && (
          <CustomToggleSwitch
            leftText="Pending CR"
            rightText="Closed CR"
            disabled={false}
            setAlignment={setAlignment}
            alignment={alignment}
            toggleWidth="min-w-fit"
            onLeftClick={() => {
              setPage(1);
            }}
            onRightClick={() => {
              setPage(1);
            }}
            leftCount={leftCount}
            RightCount={RightCount}
          />
        )}
        {role_id === 1 && (
          <IconButton
            disableRipple
            aria-label="filter"
            className="bg-white h-10 w-10 ml-4 rounded-[10px]"
            onClick={handleExportButton}
          >
            <PendingExport />
          </IconButton>
        )}
        <IconButton
          disableRipple
          aria-label="filter"
          className="bg-white h-10 w-10 ml-2 rounded-[10px]"
          onClick={handlePopoverOpen}
        >
          <PendingFilter />
        </IconButton>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              p: 2,
              width: 400,
              maxWidth: "90vw",
              borderRadius: "10px",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">Filter</Typography>
            <IconButton onClick={handlePopoverClose} size="small">
              <CrossIconTask />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ "& > *": { mb: 2, pt: 2 } }}>
            <form
              onSubmit={formik.handleSubmit}
              className="flex flex-col gap-4"
            >
                <div className="custom-task-form">
                  <Typography variant="inherit" component="label">
                    Roles
                  </Typography>
                  <Autocomplete
                    disablePortal
                    options={rolesData}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value?.id
                    }
                    sx={{
                      width: "100%",
                      mt: 1,
                      "& .MuiAutocomplete-popupIndicator": {
                        transform: "translateY(-1px)", // Adjusts arrow position upward
                        "&:hover": {
                          backgroundColor: "transparent !important", // Removes hover bg color
                        },
                      },
                    }}
                    value={
                      rolesData?.find(
                        (res) => res.id === formik.values.role
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      formik.setFieldValue(
                        "role",
                        newValue ? newValue.id : null
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Role"
                        size="small"
                        onKeyDown={(e) => {
                          const inputValue = e.target.value;
                          if (e.key === " " && inputValue.length === 0) {
                            e.preventDefault();
                          }
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#f5f5f5",
                            borderRadius: "10px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderWidth: "0px",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderWidth: "0px",
                            },
                            "&:hover": {
                              backgroundColor: "#eeeeee",
                            },
                            "& .MuiOutlinedInput-input": {
                              padding: "8.5px 14px",
                              cursor: "pointer",
                            },
                          },
                        }}
                      />
                    )}
                  />
                </div>
                <div>
                <Typography variant="inherit" component="label">
                  Request Type
                </Typography>
                <Autocomplete
                  disablePortal
                  options={requestData}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  sx={{
                    width: "100%",
                    mt: 1,
                    "& .MuiAutocomplete-popupIndicator": {
                      transform: "translateY(-1px)",
                      "&:hover": {
                        backgroundColor: "transparent !important",
                      },
                    },
                  }}
                  value={
                    requestData.find(
                      (res) => res.id === formik.values.category
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue(
                      "category",
                      newValue ? newValue?.id : null
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Request Type"
                      size="small"
                      onKeyDown={(e) => {
                        const inputValue = e.target.value;
                        if (e.key === " " && inputValue.length === 0) {
                          e.preventDefault();
                        }
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#f5f5f5",
                          borderRadius: "10px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderWidth: "0px",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderWidth: "0px",
                          },
                          "&:hover": {
                            backgroundColor: "#eeeeee",
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "8.5px 14px",
                            cursor: "pointer",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.name}
                    </li>
                  )}
                />
              </div>
          
              <div>
                <Typography variant="inherit" component="label">
                  Product
                </Typography>
                <Autocomplete
                  disablePortal
                  options={productData}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  sx={{
                    width: "100%",
                    mt: 1,
                    "& .MuiAutocomplete-popupIndicator": {
                      transform: "translateY(-1px)",
                      "&:hover": {
                        backgroundColor: "transparent !important",
                      },
                    },
                  }}
                  value={
                    productData.find(
                      (res) => res.id === formik.values.product
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue(
                      "product",
                      newValue ? newValue?.id : null
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Product"
                      size="small"
                      onKeyDown={(e) => {
                        const inputValue = e.target.value;
                        if (e.key === " " && inputValue.length === 0) {
                          e.preventDefault();
                        }
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#f5f5f5",
                          borderRadius: "10px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderWidth: "0px",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderWidth: "0px",
                          },
                          "&:hover": {
                            backgroundColor: "#eeeeee",
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "8.5px 14px",
                            cursor: "pointer",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.name}
                    </li>
                  )}
                />
              </div>
              <div>
                <Typography variant="inherit" component="label">
                  Phase
                </Typography>
                <Autocomplete
                  disablePortal
                  options={phaseData}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  sx={{
                    cursor: "pointer",
                    width: "100%",
                    mt: 1,
                    "& .MuiAutocomplete-popupIndicator": {
                      transform: "translateY(-1px)",
                      "&:hover": {
                        backgroundColor: "transparent !important",
                      },
                    },
                  }}
                  value={
                    phaseData.find((res) => res.id === formik.values.phase_id) ||
                    null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue(
                      "phase_id",
                      newValue ? newValue.id : null
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Phase"
                      size="small"
                      onKeyDown={(e) => {
                        const inputValue = e.target.value;
                        if (e.key === " " && inputValue.length === 0) {
                          e.preventDefault();
                        }
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#f5f5f5",
                          borderRadius: "10px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderWidth: "0px",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderWidth: "0px",
                          },
                          "&:hover": {
                            backgroundColor: "#eeeeee",
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "8.5px 14px",
                            cursor: "pointer",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.name}
                    </li>
                  )}
                />
              </div>
              <div>
                <Typography
                  variant="inherit"
                  component="label"
                  className="mb-2"
                >
                  Date Range
                </Typography>
                <CustomDateRangePicker
                  selectedDate={selectedDate ? selectedDate : null}
                  handleDateChange={handleDateChange}
                  months={1}
                  resetTrigger={resetCount}
                />
              </div>
              <Box display="flex" justifyContent="space-between">
                <Button
                  variant="outlined"
                  onClick={handleClearFilter}
                  sx={{ mr: 2 }}
                >
                  Clear Filter
                </Button>
                <Button variant="contained" type="submit">
                  Apply Filter
                </Button>
              </Box>
            </form>
          </Box>
        </Popover>
      </div>
    </div>
  );
};

PendingTaskHeader.propTypes = {
  taskCount: PropTypes.object,
  handleGetHistoryData: PropTypes.func,
  alignment: PropTypes.string,
  setAlignment: PropTypes.func,
  setPage: PropTypes.func,
  SearchbarPlaceholder: PropTypes.string,
  handleSearch: PropTypes.func,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  handleExportButton: PropTypes.func,
  requestData: PropTypes.array,
  phaseData: PropTypes.array,
  productData: PropTypes.array,
  handleFilterSubmit: PropTypes.func,
};

export default PendingTaskHeader;
